import { useSyncExternalStore } from "react";

/*
 * Where the docked WhatsApp panel renders, and how a modal sheet learns it is
 * open.
 *
 * The panel is not modal: the page beside it stays usable. But a modal Sheet
 * (call details, a topic) traps focus, blocks outside scrolling and pointer
 * events, and hides everything else from screen readers — so a panel drawn
 * beside it would be unreachable. Instead every open sheet offers a host
 * element inside its own content (`DockHost`), and the panel is portalled into
 * the newest one: inside the sheet as far as the browser is concerned, above
 * it on screen. With no sheet open it renders at the top of the page.
 *
 * Escape inside a sheet closes the panel first, then the sheet.
 */

const hosts: HTMLElement[] = [];
const listeners = new Set<() => void>();
let closeDock: (() => void) | null = null;

function notify(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** A sheet's host element, while the sheet is open. */
export function registerDockHost(element: HTMLElement): () => void {
  hosts.push(element);
  notify();
  return () => {
    const index = hosts.lastIndexOf(element);
    if (index >= 0) hosts.splice(index, 1);
    notify();
  };
}

/** The newest open sheet's host, or null to render at the top of the page. */
export function useDockHost(): HTMLElement | null {
  return useSyncExternalStore(
    subscribe,
    () => hosts[hosts.length - 1] ?? null,
    () => null,
  );
}

/** Set by the panel while it is open. */
export function setDockCloser(close: (() => void) | null): void {
  closeDock = close;
}

/** For a sheet's Escape: closes the panel and says so, or says it was not open. */
export function closeDockIfOpen(): boolean {
  if (!closeDock) return false;
  closeDock();
  return true;
}
